"use client";

/**
 * Listens app-wide for `bb-snap` and opens the Receipt Capture module.
 *
 *   window.dispatchEvent(new CustomEvent('bb-snap', { detail: { kind: 'receipt' | 'invoice' | 'document' } }))
 *
 * That event is the contract coπ's voice command uses ("snap a receipt",
 * "fota kvittot"); /bookkeeping and /platform use it too. An optional
 * `detail.book: 'platform'` preselects BB's own books (admins only).
 * Signed out → /signin, back to /bookkeeping with the camera open.
 * The module itself is only downloaded when it is first opened.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import type { BookTarget, DocKind } from '@/lib/bookkeeping/types';

const ReceiptCapture = dynamic(() => import('./receipt-capture'), { ssr: false });

const KINDS: DocKind[] = ['receipt', 'invoice', 'document'];

export function ReceiptSnapHost() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [open, setOpen] = useState<{ kind: DocKind; book?: BookTarget } | null>(null);
  const pending = useRef<{ kind: DocKind; book?: BookTarget } | null>(null);
  const state = useRef({ user, loading, open });
  state.current = { user, loading, open };

  useEffect(() => {
    const go = (req: { kind: DocKind; book?: BookTarget }) => {
      const s = state.current;
      if (s.open) return; // already open — keep the pages already taken
      if (s.loading) { pending.current = req; return; }
      if (!s.user) {
        router.push(`/signin?redirect=${encodeURIComponent(`/bookkeeping?snap=${req.kind}`)}`);
        return;
      }
      setOpen(req);
    };
    const onSnap = (ev: Event) => {
      ev.preventDefault(); // tells coπ the camera answered (else it opens /bookkeeping)
      const d = (ev as CustomEvent).detail || {};
      go({ kind: KINDS.includes(d.kind) ? d.kind : 'receipt', ...(d.book === 'platform' ? { book: 'platform' as const } : {}) });
    };
    window.addEventListener('bb-snap', onSnap);
    return () => window.removeEventListener('bb-snap', onSnap);
  }, [router]);

  // A snap that arrived while auth was still loading.
  useEffect(() => {
    if (loading || !pending.current) return;
    const req = pending.current;
    pending.current = null;
    if (user) setOpen(req);
    else router.push(`/signin?redirect=${encodeURIComponent(`/bookkeeping?snap=${req.kind}`)}`);
  }, [loading, user, router]);

  // Signing out closes it.
  useEffect(() => { if (!user) setOpen(null); }, [user]);

  if (!open || !user || typeof document === 'undefined') return null;
  return createPortal(<ReceiptCapture kind={open.kind} initialBook={open.book} onClose={() => setOpen(null)} />, document.body);
}
